import { useEffect, useMemo, useRef, useState } from "react";
import { trpc } from "@/lib/trpc";
import { usePermissions } from "@/hooks/usePermissions";
import { useDebounce } from "@/hooks/useDebounce";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { autoMapColumns, buildRow, mergeWithHeuristic, parseContactFile, type ParsedSheet } from "@/lib/contact-import-parse";
import { Check, FileSpreadsheet, Search, Send, Sparkles, Upload, Users, X } from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";

const FIELD_LABELS: Record<string, string> = {
  phoneNumber: "Telefone",
  name: "Nome",
  lastName: "Sobrenome",
  email: "E-mail",
  companyName: "Empresa",
  jobTitle: "Cargo",
  city: "Cidade",
  state: "UF",
  sector: "Setor",
  companyType: "Tipo",
  contactType: "Tipo de contato",
  notes: "Observações",
};

type EnqueueResult = { created: number; skippedExisting: number; skippedNoPhone: number; truncated: boolean };

function describe(r: EnqueueResult): string {
  const parts = [`${r.created} lead${r.created === 1 ? "" : "s"} na fila`];
  if (r.skippedExisting) parts.push(`${r.skippedExisting} já estavam`);
  if (r.skippedNoPhone) parts.push(`${r.skippedNoPhone} sem telefone`);
  if (r.truncated) parts.push("lista cortada em 5.000 — repita para o restante");
  return parts.join(" · ");
}

/**
 * "Adicionar leads" da fila de prospecção, sem sair da tela.
 *
 * Duas portas: uma planilha nova (que já entra com a ORIGEM do lead) ou
 * contatos que já existem. As duas terminam no mesmo lugar: oportunidades
 * "Na fila" para o SDR escolhido.
 */
export function AddLeadsDialog({
  open,
  onClose,
  pipelineId,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  pipelineId: number;
  /** Quem recebeu os leads — a tela pula para a fila dele. */
  onDone: (info: { ownerIds: number[] }) => void;
}) {
  const { can } = usePermissions();
  const canViewAll = can("crm:deals_view_all");
  const canImport = can("contacts:bulk_import");
  const utils = trpc.useUtils();
  const usersQuery = trpc.prospecting.users.list.useQuery(undefined, { enabled: open && canViewAll });
  const sourcesQuery = trpc.crm.sources.list.useQuery({}, { enabled: open });
  const users = usersQuery.data ?? [];
  const sources = sourcesQuery.data ?? [];

  const [tab, setTab] = useState<"import" | "existing">(canImport ? "import" : "existing");
  const [ownerIds, setOwnerIds] = useState<number[]>([]);
  const [sourceId, setSourceId] = useState<string>("");
  useEffect(() => {
    if (!sourceId && sources.length) {
      const outbound = sources.find((s: any) => s.legacyKey === "outbound") ?? sources[0];
      setSourceId(String(outbound.id));
    }
  }, [sources, sourceId]);

  const enqueueMut = trpc.prospecting.deals.bulkCreateFromContacts.useMutation({
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const finish = (r: EnqueueResult) => {
    toast.success(describe(r));
    utils.prospecting.queue.list.invalidate();
    utils.prospecting.queue.batches.invalidate();
    utils.prospecting.report.summary.invalidate();
    onDone({ ownerIds });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send className="h-4 w-4" /> Adicionar leads à fila</DialogTitle>
          <DialogDescription>
            Cada contato com telefone vira um lead "Na fila". Quem já está em prospecção é pulado.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => setTab(v as any)} className="flex-1 min-h-0 flex flex-col">
          <TabsList className="self-start">
            {canImport && <TabsTrigger value="import"><FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Importar planilha</TabsTrigger>}
            <TabsTrigger value="existing"><Users className="h-3.5 w-3.5 mr-1.5" /> Contatos existentes</TabsTrigger>
          </TabsList>

          {/* Comum às duas abas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <div className="space-y-1">
              <Label className="text-xs">Origem dos leads</Label>
              <Select value={sourceId} onValueChange={setSourceId}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Origem" /></SelectTrigger>
                <SelectContent>
                  {sources.map((s: any) => (
                    <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {canViewAll && users.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs">Comercial responsável (vários = rodízio; nenhum = você)</Label>
                <div className="flex flex-wrap gap-1.5">
                  {users.map((u) => {
                    const on = ownerIds.includes(u.id);
                    return (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => setOwnerIds((prev) => (on ? prev.filter((x) => x !== u.id) : [...prev, u.id]))}
                        className={`px-2.5 py-1 rounded-full text-xs border ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"}`}
                      >
                        {u.name ?? u.email}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {ownerIds.length === 1
                    ? "A lista fica com esse comercial: os leads entram na fila dele e ele passa a ser o responsável por cada contato — inclusive na Inbox, quando o lead responder."
                    : ownerIds.length > 1
                      ? "Os leads são distribuídos em rodízio; cada contato fica com quem o recebeu."
                      : "Os leads entram na sua fila. Contato que já tem um comercial responsável continua com ele."}
                </p>
              </div>
            )}
          </div>

          {canImport && (
            <TabsContent value="import" className="flex-1 min-h-0 mt-3 flex flex-col data-[state=inactive]:hidden">
              <ImportTab
                pipelineId={pipelineId}
                ownerIds={ownerIds}
                sourceId={sourceId ? Number(sourceId) : null}
                onFinish={finish}
                onCancel={onClose}
              />
            </TabsContent>
          )}
          <TabsContent value="existing" className="flex-1 min-h-0 mt-3 flex flex-col data-[state=inactive]:hidden">
            <ExistingTab
              pending={enqueueMut.isPending}
              onCancel={onClose}
              onConfirm={(contactIds) =>
                enqueueMut.mutate(
                  { pipelineId, contactIds, ownerUserIds: ownerIds, sourceId: sourceId ? Number(sourceId) : null },
                  { onSuccess: finish },
                )
              }
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// ─── Aba: importar planilha ──────────────────────────────────────────────────

function ImportTab({
  pipelineId,
  ownerIds,
  sourceId,
  onFinish,
  onCancel,
}: {
  pipelineId: number;
  ownerIds: number[];
  sourceId: number | null;
  onFinish: (r: EnqueueResult) => void;
  onCancel: () => void;
}) {
  const utils = trpc.useUtils();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [sheet, setSheet] = useState<ParsedSheet | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [listName, setListName] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  const importMut = trpc.contacts.bulkImport.useMutation();
  const enqueueMut = trpc.prospecting.deals.bulkCreateFromContacts.useMutation();
  const suggestMut = trpc.prospecting.import.suggestMapping.useMutation();
  const [aiState, setAiState] = useState<"idle" | "running" | "done" | "off">("idle");
  // Qual arquivo a IA está analisando e quais colunas o usuário já mexeu à
  // mão: resposta atrasada de outro arquivo é descartada, e edição manual
  // não é sobrescrita.
  const aiRequestRef = useRef(0);
  const touchedRef = useRef<Set<string>>(new Set());

  async function pick(f: File | undefined) {
    if (!f) return;
    try {
      const parsed = await parseContactFile(f);
      if (parsed.rows.length === 0) return toast.error("Arquivo vazio ou sem dados válidos");
      setFile(f);
      setSheet(parsed);
      // Nome da lista sugerido a partir do arquivo — é o que aparece no filtro da fila.
      setListName((prev) => prev || f.name.replace(/\.(csv|xlsx|xls)$/i, "").replace(/[_-]+/g, " ").trim());
      // Heurística local na hora; a IA refina em seguida, se estiver configurada.
      const heuristic = autoMapColumns(parsed.headers, parsed.rows);
      setMapping(heuristic);
      touchedRef.current = new Set();
      const requestId = ++aiRequestRef.current;
      setAiState("running");
      const columns = parsed.headers.slice(0, 80).map((h) => ({
        header: h.slice(0, 120),
        samples: parsed.rows
          .map((r) => (r[h] ?? "").trim())
          .filter(Boolean)
          .slice(0, 3)
          .map((v) => v.slice(0, 40)),
      }));
      suggestMut.mutate(
        { columns },
        {
          onSuccess: (m) => {
            if (requestId !== aiRequestRef.current) return; // outro arquivo já foi escolhido
            if (!m) { setAiState("off"); return; }
            const merged = mergeWithHeuristic(m, heuristic, parsed.headers, parsed.rows);
            setMapping((prev) => {
              const next = { ...merged };
              for (const col of touchedRef.current) if (col in prev) next[col] = prev[col];
              return next;
            });
            setAiState("done");
          },
          onError: () => { if (requestId === aiRequestRef.current) setAiState("off"); },
        },
      );
    } catch (e: any) {
      toast.error(e?.message ?? "Não foi possível ler o arquivo");
    }
  }

  const mapped = useMemo(() => Object.entries(mapping).filter(([, f]) => f !== "__ignore__"), [mapping]);
  const hasName = mapped.some(([, f]) => f === "name");
  const hasPhone = mapped.some(([, f]) => f === "phoneNumber");

  const rowsToSend = useMemo(() => {
    if (!sheet) return [];
    return sheet.rows.map((r) => buildRow(r, mapping, sheet.headers)).filter((r) => r.name && r.phoneNumber);
  }, [sheet, mapping]);
  const phoneColumns = mapped.filter(([, f]) => f === "phoneNumber").map(([c]) => c);

  async function run() {
    if (!file || !sheet) return;
    if (!hasName || !hasPhone) return toast.error("A planilha precisa de uma coluna de nome e uma de telefone");
    if (rowsToSend.length === 0) return toast.error("Nenhuma linha com nome e telefone");
    try {
      setBusy("Importando contatos...");
      const imported = await importMut.mutateAsync({
        rows: rowsToSend as any,
        duplicateStrategy: "update",
        fileName: file.name,
        displayName: listName.trim() || undefined,
        fileSize: file.size,
        mimeType: file.type || undefined,
        mappingSnapshot: mapping,
      });
      utils.contacts.list.invalidate();
      utils.contacts.importsList.invalidate();
      if (!imported.batchId) throw new Error("Importação sem lote");
      setBusy("Colocando na fila...");
      const r = await enqueueMut.mutateAsync({
        pipelineId,
        importBatchId: imported.batchId,
        ownerUserIds: ownerIds,
        sourceId,
      });
      onFinish(r);
    } catch (e: any) {
      toast.error(toUserFriendlyError(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex flex-col gap-3 flex-1 min-h-0">
      {!sheet ? (
        <label
          className="flex-1 min-h-[180px] border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-muted/40 text-center p-6"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files?.[0]); }}
        >
          <Upload className="h-7 w-7 text-muted-foreground" />
          <div className="text-sm font-medium">Arraste a planilha ou clique para escolher</div>
          <div className="text-xs text-muted-foreground">CSV, XLSX ou XLS. Precisa ter nome e telefone.</div>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
        </label>
      ) : (
        <div className="space-y-3 overflow-y-auto pr-1 flex-1 min-h-0">
          <div className="flex items-center gap-2 text-sm">
            <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium truncate">{file?.name}</span>
            <Badge variant="secondary">{sheet.rows.length} linhas</Badge>
            <Badge variant={rowsToSend.length ? "default" : "destructive"}>{rowsToSend.length} com nome e telefone</Badge>
            <Button size="sm" variant="ghost" className="h-7 ml-auto" onClick={() => { setSheet(null); setFile(null); }}>
              <X className="h-3.5 w-3.5 mr-1" /> Trocar arquivo
            </Button>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Nome da lista</Label>
            <Input
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              maxLength={200}
              placeholder="Ex.: Prefeituras MT — Compras 2026"
              className="h-9"
            />
            <p className="text-[11px] text-muted-foreground">
              É por esse nome que o comercial filtra a fila quando quer trabalhar um público-alvo de cada vez.
            </p>
          </div>

          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1.5">
              Colunas reconhecidas (ajuste se algo veio errado)
              {aiState === "running" && (
                <span className="inline-flex items-center gap-1 text-primary"><Sparkles className="h-3 w-3 animate-pulse" /> IA reconhecendo…</span>
              )}
              {aiState === "done" && (
                <span className="inline-flex items-center gap-1 text-primary"><Sparkles className="h-3 w-3" /> reconhecidas pela IA</span>
              )}
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {sheet.headers.map((h) => (
                <div key={h} className="flex items-center gap-2 text-xs">
                  <span className="truncate flex-1 text-muted-foreground" title={h}>{h}</span>
                  <Select value={mapping[h] ?? "__ignore__"} onValueChange={(v) => { touchedRef.current.add(h); setMapping((m) => ({ ...m, [h]: v })); }}>
                    <SelectTrigger className="h-7 w-[150px] text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__ignore__">Ignorar</SelectItem>
                      {Object.entries(FIELD_LABELS).map(([k, l]) => (
                        <SelectItem key={k} value={k}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            {(!hasName || !hasPhone) && (
              <p className="text-xs text-destructive">Marque qual coluna é o Nome e qual é o Telefone.</p>
            )}
            {phoneColumns.length > 1 && (
              <p className="text-xs text-muted-foreground">
                Telefone em {phoneColumns.length} colunas ({phoneColumns.join(", ")}): em cada linha vale a primeira preenchida.
              </p>
            )}
          </div>

          <div className="text-xs text-muted-foreground">
            Prévia: {sheet.rows.slice(0, 3).map((r, i) => {
              const b = buildRow(r, mapping, sheet.headers);
              return <span key={i} className="mr-3">{b.name ?? "?"} · {b.phoneNumber ?? "sem telefone"}</span>;
            })}
          </div>
        </div>
      )}

      <DialogFooter className="shrink-0 pt-2 border-t">
        <Button variant="outline" onClick={onCancel} disabled={!!busy}>Cancelar</Button>
        <Button onClick={run} disabled={!sheet || !!busy || rowsToSend.length === 0}>
          <Send className="h-4 w-4 mr-1.5" /> {busy ?? `Importar e enfileirar ${rowsToSend.length || ""}`}
        </Button>
      </DialogFooter>
    </div>
  );
}

// ─── Aba: contatos existentes ────────────────────────────────────────────────

function ExistingTab({
  pending,
  onCancel,
  onConfirm,
}: {
  pending: boolean;
  onCancel: () => void;
  onConfirm: (contactIds: number[]) => void;
}) {
  const [search, setSearch] = useState("");
  const [tagId, setTagId] = useState<string>("all");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const debounced = useDebounce(search, 300);

  const tagsQuery = trpc.tags.list.useQuery();
  const listQuery = trpc.contacts.list.useQuery({
    search: debounced || undefined,
    tagId: tagId !== "all" ? Number(tagId) : undefined,
    limit: 100,
    skipTotal: true,
  });
  const rows = (listQuery.data?.contacts ?? []) as any[];

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  const allVisibleSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));

  return (
    <div className="flex flex-col gap-2 h-full min-h-0">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nome, telefone ou empresa" className="h-9 pl-8" />
        </div>
        <Select value={tagId} onValueChange={setTagId}>
          <SelectTrigger className="h-9 w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as tags</SelectItem>
            {(tagsQuery.data ?? []).map((t: any) => (
              <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
        <button
          type="button"
          className="underline-offset-2 hover:underline"
          onClick={() =>
            setSelected((prev) => {
              const next = new Set(prev);
              if (allVisibleSelected) rows.forEach((r) => next.delete(r.id));
              else rows.forEach((r) => next.add(r.id));
              return next;
            })
          }
        >
          {allVisibleSelected ? "Desmarcar os visíveis" : `Marcar os ${rows.length} visíveis`}
        </button>
        <span>{selected.size} selecionado{selected.size === 1 ? "" : "s"}</span>
      </div>

      <div className="flex-1 min-h-[160px] overflow-y-auto border rounded-md divide-y">
        {listQuery.isLoading ? (
          <div className="p-4 text-sm text-muted-foreground">Carregando...</div>
        ) : rows.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground">Nenhum contato encontrado.</div>
        ) : (
          rows.map((c) => {
            const on = selected.has(c.id);
            const nome = [c.name, c.lastName].filter(Boolean).join(" ");
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => toggle(c.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm hover:bg-muted/40 ${on ? "bg-primary/5" : ""}`}
              >
                <span className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${on ? "bg-primary border-primary text-primary-foreground" : ""}`}>
                  {on && <Check className="h-3 w-3" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="font-medium truncate block">{nome}</span>
                  <span className="text-xs text-muted-foreground truncate block">
                    {[c.phoneNumber, c.companyName].filter(Boolean).join(" · ") || "sem telefone"}
                  </span>
                </span>
              </button>
            );
          })
        )}
      </div>

      <DialogFooter className="shrink-0 pt-2 border-t">
        <Button variant="outline" onClick={onCancel} disabled={pending}>Cancelar</Button>
        <Button onClick={() => onConfirm(Array.from(selected))} disabled={pending || selected.size === 0}>
          <Send className="h-4 w-4 mr-1.5" /> {pending ? "Adicionando..." : `Adicionar ${selected.size || ""} à fila`}
        </Button>
      </DialogFooter>
    </div>
  );
}
